/**
 * Success Modal Component
 * Shows cool popup when email is sent successfully
 */

import React, { useEffect } from 'react';
import styled from 'styled-components';
import { FaCheckCircle } from 'react-icons/fa';

const ModalOverlay = styled.div<{ $isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: ${({ $isVisible }) => ($isVisible ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(3px);
  animation: ${({ $isVisible }) => ($isVisible ? 'fadeIn' : 'fadeOut')} 0.3s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes fadeOut {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }
`;

const ModalContent = styled.div`
  background: ${({ theme }) => theme.surface};
  border-radius: 20px;
  padding: 3rem 2.5rem;
  text-align: center;
  max-width: 500px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: popIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  border: ${({ theme }) => theme.cardBorder};

  @keyframes popIn {
    0% {
      opacity: 0;
      transform: scale(0.7) translateY(20px);
    }
    50% {
      transform: scale(1.05);
    }
    100% {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }

  @media (max-width: 480px) {
    padding: 2rem 1.5rem;
    margin: 0 1rem;
  }
`;

const CheckCircleIcon = styled(FaCheckCircle)`
  font-size: 4rem;
  color: #4caf80;
  margin-bottom: 1.5rem;
  animation: bounceIn 0.6s ease;

  @keyframes bounceIn {
    0% {
      opacity: 0;
      transform: scale(0);
    }
    50% {
      transform: scale(1.1);
    }
    100% {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const Title = styled.h2`
  font-size: 1.8rem;
  color: ${({ theme }) => theme.headingColor};
  margin: 0 0 0.5rem;
  font-weight: 700;
`;

const Message = styled.p`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.text};
  margin: 0;
  line-height: 1.6;
`;

const SubMessage = styled.p`
  font-size: 0.95rem;
  color: ${({ theme }) => theme.text}99;
  margin: 1rem 0 0;
  font-style: italic;
`;

interface SuccessModalProps {
  isVisible: boolean;
  onClose?: () => void;
  onAutoClose?: () => void; // Called when modal auto-closes
  autoCloseDuration?: number; // in milliseconds
}

const SuccessModal: React.FC<SuccessModalProps> = ({
  isVisible,
  onClose,
  onAutoClose,
  autoCloseDuration = 4000,
}) => {
  useEffect(() => {
    if (isVisible && autoCloseDuration > 0) {
      const timer = setTimeout(() => {
        if (onAutoClose) {
          onAutoClose();
        } else if (onClose) {
          onClose();
        }
      }, autoCloseDuration);

      return () => clearTimeout(timer);
    }
  }, [isVisible, autoCloseDuration, onClose, onAutoClose]);

  return (
    <ModalOverlay $isVisible={isVisible} onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CheckCircleIcon />
        <Title>Thank you!</Title>
        <Message>Thanks for reaching me out, I will get to you soon.</Message>
        <SubMessage>I appreciate your message and will respond as quickly as possible.</SubMessage>
      </ModalContent>
    </ModalOverlay>
  );
};

export default SuccessModal;
